import { useState } from 'react'
import { Users, Clock, FileText, Headphones, CheckSquare, TrendingUp, Calendar, MessageSquare, AlertTriangle } from 'lucide-react'
import { useBrandColors } from '../../hooks/useBrandColors'
import { useNavigate } from 'react-router-dom'

const EVENTS = [
  { date: '2025-04-02', type: 'today',     label: 'Today' },
  { date: '2025-04-05', type: 'review',    label: 'Kim Park - Review' },
  { date: '2025-04-10', type: 'incident',  label: 'Incident Follow-up' },
  { date: '2025-04-12', type: 'start',     label: 'Alex Turner starts' },
  { date: '2025-04-14', type: 'event',     label: 'Schedule Change' },
  { date: '2025-04-15', type: 'start',     label: 'Priya Sharma starts' },
  { date: '2025-04-22', type: 'event',     label: 'Appreciation Week' },
  { date: '2025-04-23', type: 'pto',       label: 'Jamie - PTO' },
  { date: '2025-04-24', type: 'pto',       label: 'Jamie - PTO' },
  { date: '2025-04-25', type: 'pto',       label: 'Jamie - PTO' },
  { date: '2025-04-28', type: 'review',    label: 'Marcus - Check-in' },
  { date: '2025-05-01', type: 'start',     label: 'Carlos Mendez starts' },
]

const WEEK_EVENTS = [
  { day: 0, type: 'pto',      label: 'Jamie - PTO (all week)',    time: 'All day' },
  { day: 1, type: 'review',   label: 'Marcus T. Check-in',        time: '9:00 AM' },
  { day: 1, type: 'incident', label: 'Incident Report Review',    time: '2:00 PM' },
  { day: 2, type: 'event',    label: 'Team Standup',              time: '8:30 AM' },
  { day: 3, type: 'review',   label: 'Q2 Performance Reviews',    time: '10:00 AM' },
  { day: 4, type: 'start',    label: 'Alex Turner - Start Date',  time: 'All day' },
  { day: 5, type: 'event',    label: 'Employee Appreciation Week',time: 'All week' },
]

export default function AdminDashboard() {
  const { accent, accentLight, accentBg } = useBrandColors()
  const navigate = useNavigate()
  const [calView, setCalView] = useState('month')
  const [currentMonth, setCurrentMonth] = useState(new Date(2025, 3, 1))

  const stats = [
    { label: 'Total Employees',  value: '18',  pill: '+2 this month',   pillColor: '#B8734A', route: '/admin/employees', Icon: Users, iconColor: '#B8734A' },
    { label: 'Pending Approvals',value: '10',  pill: '4 PTO · 6 time',  pillColor: '#D9C3A3', route: '/admin/approvals', badge: 10, Icon: CheckSquare, iconColor: '#D9C3A3' },
    { label: 'Unsigned Policies',value: '7',   pill: 'Needs attention', pillColor: '#A0392B', route: '/admin/documents',   Icon: FileText,   iconColor: '#A0392B' },
    { label: 'Avg Attendance',   value: '94%', pill: 'Above target',    pillColor: '#6B7D6B', route: '/admin/performance', Icon: TrendingUp, iconColor: '#6B7D6B' },
    { label: 'Unread Messages',  value: '3',   pill: '2 from employees',pillColor: '#D9C3A3', route: '/admin/memos', badge: 3 },
  ]

  const alerts = [
    { title: 'Marcus T. - 3rd attendance warning', sub: 'Requires formal documentation · Operations', time: 'Today',     color: '#A0392B', route: '/admin/performance' },
    { title: 'Vehicle incident report filed',       sub: 'J. Collins · Apr 1 · Awaiting review',      time: 'Yesterday', color: '#A0392B', route: '/admin/performance' },
    { title: '7 employees have unsigned policies',  sub: 'Handbook 2025 - send reminder?',             time: 'Apr 1',     color: accent,    route: '/admin/documents' },
    { title: '10 approvals pending',                sub: '4 PTO requests · 6 timesheets',              time: 'Apr 2',     color: '#C4843A', route: '/admin/approvals' },
    { title: 'Alex Turner starts Apr 12',           sub: 'Onboarding packet sent · 75% complete',      time: 'Apr 2',     color: '#4A6B8A', route: '/admin/employees' },
  ]

  const quickActions = [
    { label: 'New Employee',      icon: '+', sub: 'Add & send onboarding',   route: '/admin/employees', color: accent },
    { label: 'Send Memo',         icon: 'M', sub: 'Compose announcement',    route: '/admin/memos',     color: '#4A6B8A' },
    { label: 'Review Approvals',  icon: '!', sub: '10 items pending',        route: '/admin/approvals', color: '#C4843A' },
    { label: 'View Performance',  icon: 'P', sub: 'Reviews & warnings',      route: '/admin/performance',color: '#4A7C59' },
    { label: 'Document Library',  icon: 'D', sub: '7 need signatures',       route: '/admin/documents', color: '#A0392B' },
    { label: 'Client Config',     icon: 'C', sub: 'Branding & settings',     route: '/admin/config',    color: '#6B7D6B' },
  ]

  const recognition = [
    { name: 'Jamie Mitchell', reason: 'Above & beyond - client event coverage', date: 'Apr 1',  init: 'JM' },
    { name: 'Dana Lee',       reason: 'Perfect attendance - Q1 2025',           date: 'Mar 31', init: 'DL' },
    { name: 'Sam Rivera',     reason: 'Onboarded 3 new hires flawlessly',       date: 'Mar 28', init: 'SR' },
  ]

  const eventColor = t => ({
    today:    accent,
    start:    '#4A7C59',
    pto:      '#D9C3A3',
    review:   '#4A6B8A',
    incident: '#A0392B',
    event:    '#6B7D6B',
  }[t] || '#6B7D6B')

  const eventLabel = t => ({
    today: 'Today', start: 'New Hire', pto: 'PTO',
    review: 'Review', incident: 'Incident', event: 'Event'
  }[t] || t)

  // Month calendar helpers
  const year = currentMonth.getFullYear()
  const month = currentMonth.getMonth()
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`
    return EVENTS.filter(e => e.date === dateStr)
  }

  const weekDays = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
  const weekDates = Array.from({length:7},(_,i) => {
    const d = new Date(2025, 3, 20 + i)
    return { date: d, dayIdx: i, label: weekDays[d.getDay()] }
  })

  return (
    <div style={{fontFamily:"'Montserrat',sans-serif"}}>

      {/* Header */}
      <div style={{marginBottom:20,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div>
          <h1 style={{fontFamily:'Georgia,serif',fontSize:'1.4rem',color:'#2B2B2B',fontWeight:600}}>Admin Dashboard</h1>
          <p style={{fontSize:11,color:'#6B7D6B',marginTop:2}}>Wednesday, April 2, 2025 · 18 active employees · 3 onboarding</p>
        </div>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          <div style={{position:'relative',cursor:'pointer'}} onClick={()=>navigate('/admin/memos')}>
            <div style={{width:36,height:36,borderRadius:'50%',background:'#fff',border:'1px solid #EDE6DA',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16}}>
              M
            </div>
            <div style={{position:'absolute',top:-4,right:-4,width:16,height:16,borderRadius:'50%',background:'#4A6B8A',color:'#fff',fontSize:9,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center'}}>3</div>
          </div>
          <div style={{position:'relative',cursor:'pointer'}} onClick={()=>navigate('/admin/approvals')}>
            <div style={{width:36,height:36,borderRadius:'50%',background:'#fff',border:'1px solid #EDE6DA',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16}}>
              A
            </div>
            <div style={{position:'absolute',top:-4,right:-4,width:16,height:16,borderRadius:'50%',background:'#C4843A',color:'#fff',fontSize:9,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center'}}>10</div>
          </div>
        </div>
      </div>

      {/* Clickable Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10,marginBottom:20}}>
        {stats.map(s => (
          <div key={s.label} onClick={()=>navigate(s.route)}
            style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,padding:'14px 16px',cursor:'pointer',transition:'all 0.15s',position:'relative'}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=accent;e.currentTarget.style.transform='translateY(-2px)'}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor='#EDE6DA';e.currentTarget.style.transform='translateY(0)'}}>
            {s.badge && <div style={{position:'absolute',top:8,right:8,width:18,height:18,borderRadius:'50%',background:s.pillColor,color:'#fff',fontSize:9,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center'}}>{s.badge}</div>}
            <div style={{fontSize:9,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:500,marginBottom:4}}>{s.label}</div>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:'1.5rem',color:'#2B2B2B',fontWeight:600,lineHeight:1}}>{s.value}</div>
              {s.Icon && <div style={{width:36,height:36,borderRadius:8,background:s.iconColor+'18',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><s.Icon size={18} color={s.iconColor} strokeWidth={1.75}/></div>}
            </div>
            <div style={{marginTop:4,display:'inline-block',padding:'1px 6px',borderRadius:8,fontSize:10,fontWeight:600,background:s.pillColor+'22',color:s.pillColor}}>{s.pill}</div>
          </div>
        ))}
      </div>

      {/* Main grid - Calendar + Alerts */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 300px',gap:14,marginBottom:14}}>

        {/* Calendar */}
        <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
          <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              {calView === 'month' && (
                <>
                  <button onClick={()=>setCurrentMonth(new Date(year, month-1, 1))} style={{background:'none',border:'1px solid #EDE6DA',borderRadius:4,width:24,height:24,cursor:'pointer',fontSize:12,display:'flex',alignItems:'center',justifyContent:'center'}}>{'<'}</button>
                  <div style={{fontFamily:'Georgia,serif',fontSize:14,fontWeight:600,color:'#2B2B2B',minWidth:140,textAlign:'center'}}>{monthName}</div>
                  <button onClick={()=>setCurrentMonth(new Date(year, month+1, 1))} style={{background:'none',border:'1px solid #EDE6DA',borderRadius:4,width:24,height:24,cursor:'pointer',fontSize:12,display:'flex',alignItems:'center',justifyContent:'center'}}>{'>'}</button>
                </>
              )}
              {calView === 'week' && (
                <div style={{fontFamily:'Georgia,serif',fontSize:14,fontWeight:600,color:'#2B2B2B'}}>Week of Apr 20 - 26, 2025</div>
              )}
            </div>
            <div style={{display:'flex',background:'#F7F2EB',border:'1px solid #EDE6DA',borderRadius:6,overflow:'hidden'}}>
              {['month','week'].map((v,i)=>(
                <button key={v} onClick={()=>setCalView(v)}
                  style={{padding:'4px 12px',border:'none',borderRight:i===0?'1px solid #EDE6DA':'none',background:calView===v?accent:'transparent',color:calView===v?'#fff':'#4A4A4A',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif",textTransform:'capitalize',transition:'all 0.15s'}}>
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Month View */}
          {calView === 'month' && (
            <div style={{padding:'12px'}}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',marginBottom:4}}>
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d=>(
                  <div key={d} style={{textAlign:'center',fontSize:9,color:'#6B7D6B',fontWeight:600,letterSpacing:'0.06em',textTransform:'uppercase',padding:'4px 0'}}>{d}</div>
                ))}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2}}>
                {Array.from({length: firstDay}).map((_,i)=>(
                  <div key={`empty-${i}`} style={{minHeight:64,borderRadius:4}}/>
                ))}
                {Array.from({length: daysInMonth}).map((_,i)=>{
                  const day = i + 1
                  const dayEvents = getEventsForDay(day)
                  const isToday = day === 2 && month === 3
                  return (
                    <div key={day} style={{minHeight:64,borderRadius:4,border:'1px solid #F0EBE2',padding:'4px',background:isToday?accentBg:'#fff',cursor:dayEvents.length?'pointer':'default'}}
                      onMouseEnter={e=>{if(dayEvents.length)e.currentTarget.style.background=accentBg}}
                      onMouseLeave={e=>{if(!isToday)e.currentTarget.style.background='#fff'}}>
                      <div style={{fontSize:11,fontWeight:isToday?700:400,marginBottom:2,width:20,height:20,borderRadius:'50%',background:isToday?accent:'transparent',color:isToday?'#fff':'#4A4A4A',display:'flex',alignItems:'center',justifyContent:'center'}}>{day}</div>
                      {dayEvents.slice(0,2).map((ev,ei)=>(
                        <div key={ei} style={{fontSize:8,padding:'1px 4px',borderRadius:3,background:eventColor(ev.type)+'22',color:eventColor(ev.type),fontWeight:600,marginBottom:1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {ev.label}
                        </div>
                      ))}
                      {dayEvents.length > 2 && <div style={{fontSize:8,color:'#8A9E8A'}}>+{dayEvents.length-2} more</div>}
                    </div>
                  )
                })}
              </div>
              {/* Legend */}
              <div style={{display:'flex',gap:12,marginTop:10,paddingTop:10,borderTop:'1px solid #EDE6DA',flexWrap:'wrap'}}>
                {[['start','New Hire'],['pto','PTO'],['review','Review'],['incident','Incident'],['event','Event']].map(([type,label])=>(
                  <div key={type} style={{display:'flex',alignItems:'center',gap:4,fontSize:10,color:'#6B7D6B'}}>
                    <div style={{width:8,height:8,borderRadius:2,background:eventColor(type)}}/>
                    {label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Week View */}
          {calView === 'week' && (
            <div style={{padding:'12px'}}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:4}}>
                {weekDates.map((wd,i)=>{
                  const dayEvents = WEEK_EVENTS.filter(e=>e.day===i)
                  const isToday = i === 0
                  return (
                    <div key={i} style={{minHeight:160,borderRadius:6,border:`1px solid ${isToday?accent:'#EDE6DA'}`,overflow:'hidden'}}>
                      <div style={{padding:'6px 8px',background:isToday?accent:'#F7F2EB',textAlign:'center',borderBottom:'1px solid #EDE6DA'}}>
                        <div style={{fontSize:9,textTransform:'uppercase',letterSpacing:'0.08em',color:isToday?'rgba(255,255,255,0.7)':'#6B7D6B',fontWeight:600}}>{wd.label}</div>
                        <div style={{fontSize:16,fontWeight:700,color:isToday?'#fff':'#2B2B2B',fontFamily:'Georgia,serif'}}>{wd.date.getDate()}</div>
                      </div>
                      <div style={{padding:'6px',display:'flex',flexDirection:'column',gap:3}}>
                        {dayEvents.map((ev,ei)=>(
                          <div key={ei} style={{padding:'3px 6px',borderRadius:4,background:eventColor(ev.type)+'22',borderLeft:`2px solid ${eventColor(ev.type)}`}}>
                            <div style={{fontSize:9,fontWeight:600,color:eventColor(ev.type)}}>{ev.time}</div>
                            <div style={{fontSize:9,color:'#4A4A4A',lineHeight:1.3}}>{ev.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Alerts */}
        <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden',display:'flex',flexDirection:'column'}}>
          <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Alerts & Activity</div>
            <span style={{background:'rgba(160,57,43,0.1)',color:'#A0392B',fontSize:10,padding:'2px 8px',borderRadius:8,fontWeight:600}}>2 urgent</span>
          </div>
          <div style={{flex:1,overflowY:'auto'}}>
            {alerts.map((a,i) => (
              <div key={i} onClick={()=>navigate(a.route)}
                style={{padding:'10px 16px',borderBottom:'1px solid #EDE6DA',cursor:'pointer',borderLeft:`3px solid ${a.color}`,transition:'background 0.1s'}}
                onMouseEnter={e=>e.currentTarget.style.background='#F7F2EB'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:2}}>
                  <div style={{fontSize:11,fontWeight:600,color:'#2B2B2B',flex:1,paddingRight:8,lineHeight:1.4}}>{a.title}</div>
                  <div style={{fontSize:9,color:'#8A9E8A',flexShrink:0}}>{a.time}</div>
                </div>
                <div style={{fontSize:10,color:'#6B7D6B'}}>{a.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom grid - Quick Actions + Recognition */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>

        {/* Quick Actions */}
        <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
          <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA'}}>
            <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Quick Actions</div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,padding:12}}>
            {quickActions.map(a=>(
              <button key={a.label} onClick={()=>navigate(a.route)}
                style={{padding:'12px',border:'1px solid #EDE6DA',borderRadius:8,background:'#F7F2EB',cursor:'pointer',textAlign:'left',transition:'all 0.15s',fontFamily:"'Montserrat',sans-serif"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=a.color;e.currentTarget.style.background=a.color+'12'}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor='#EDE6DA';e.currentTarget.style.background='#F7F2EB'}}>
                <div style={{width:28,height:28,borderRadius:6,background:a.color+'22',color:a.color,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,marginBottom:6}}>{a.icon}</div>
                <div style={{fontSize:12,fontWeight:600,color:'#2B2B2B',marginBottom:2}}>{a.label}</div>
                <div style={{fontSize:10,color:'#8A9E8A'}}>{a.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Recognition */}
        <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
          <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Recent Recognition</div>
            <button onClick={()=>navigate('/admin/performance')} style={{fontSize:11,color:accent,background:'none',border:'none',cursor:'pointer',fontFamily:"'Montserrat',sans-serif",fontWeight:500}}>View all</button>
          </div>
          {recognition.map((r,i) => (
            <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',borderBottom:'1px solid #EDE6DA',cursor:'pointer'}}
              onMouseEnter={e=>e.currentTarget.style.background='#F7F2EB'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{width:32,height:32,borderRadius:'50%',background:accentBg,color:accent,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,flexShrink:0}}>{r.init}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:500,color:'#2B2B2B'}}>{r.name}</div>
                <div style={{fontSize:11,color:'#6B7D6B'}}>{r.reason}</div>
              </div>
              <div style={{fontSize:10,color:'#8A9E8A',flexShrink:0}}>{r.date}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
